import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export interface Organization {
  name: string;
  url: string;
  logo: string;
  sameAs?: string[];
  contactPoint?: {
    contactType: string;
    email?: string;
    telephone?: string;
  };
}

export interface SportsEvent {
  name: string;
  description: string;
  startDate: string;
  endDate?: string;
  location: {
    name: string;
    address?: string;
  };
  organizer?: {
    name: string;
    url?: string;
  };
}

export interface Breadcrumb {
  name: string;
  url: string;
}

@Injectable({
  providedIn: 'root',
})
export class StructuredDataService {
  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  /**
   * Insert structured data JSON-LD into page
   */
  private insertStructuredData(data: any, id: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return; // Skip during SSR
    }

    // Remove existing script if present
    const existingScript = document.getElementById(id);
    if (existingScript) {
      existingScript.remove();
    }

    // Create new script element
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
  }

  /**
   * Add Organization structured data
   */
  addOrganization(org: Organization): void {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: org.name,
      url: org.url,
      logo: org.logo,
      ...(org.sameAs && { sameAs: org.sameAs }),
      ...(org.contactPoint && { contactPoint: org.contactPoint }),
    };

    this.insertStructuredData(schema, 'organization-schema');
  }

  /**
   * Add SportsOrganization structured data for cricket clubs
   */
  addSportsOrganization(club: {
    name: string;
    url: string;
    sport: string;
    logo?: string;
    foundingDate?: string;
    address?: string;
  }): void {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'SportsOrganization',
      name: club.name,
      url: club.url,
      sport: club.sport,
      ...(club.logo && { logo: club.logo }),
      ...(club.foundingDate && { foundingDate: club.foundingDate }),
      ...(club.address && { address: club.address }),
    };

    this.insertStructuredData(schema, 'sports-organization-schema');
  }

  /**
   * Add SportsEvent structured data for matches/tournaments
   */
  addSportsEvent(event: SportsEvent): void {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'SportsEvent',
      name: event.name,
      description: event.description,
      startDate: event.startDate,
      ...(event.endDate && { endDate: event.endDate }),
      location: {
        '@type': 'Place',
        name: event.location.name,
        ...(event.location.address && { address: event.location.address }),
      },
      ...(event.organizer && {
        organizer: {
          '@type': 'Organization',
          name: event.organizer.name,
          ...(event.organizer.url && { url: event.organizer.url }),
        },
      }),
    };

    this.insertStructuredData(schema, 'sports-event-schema');
  }

  /**
   * Add BreadcrumbList structured data
   */
  addBreadcrumbs(breadcrumbs: Breadcrumb[]): void {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.name,
        item: crumb.url,
      })),
    };

    this.insertStructuredData(schema, 'breadcrumb-schema');
  }

  /**
   * Add WebSite structured data with search action
   */
  addWebSite(config: { name: string; url: string; searchUrl?: string }): void {
    const schema: any = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: config.name,
      url: config.url,
    };

    if (config.searchUrl) {
      schema.potentialAction = {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${config.searchUrl}?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      };
    }

    this.insertStructuredData(schema, 'website-schema');
  }

  /**
   * Add FAQPage structured data for Google rich snippets
   */
  addFAQPage(faqs: { question: string; answer: string }[]): void {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    };

    this.insertStructuredData(schema, 'faq-schema');
  }

  /**
   * Remove structured data by ID
   */
  removeStructuredData(id: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const script = document.getElementById(id);
    if (script) {
      script.remove();
    }
  }

  /**
   * Remove all structured data
   */
  removeAllStructuredData(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const scripts = document.querySelectorAll('script[type="application/ld+json"]');
    scripts.forEach((script) => script.remove());
  }
}
